import React, { useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InsightsService from "../services/InsightsService";

function fmt(value) {
  const n = Number(value || 0);
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function fmtDate(value) {
  if (!value) return "—";
  try { return new Date(value).toLocaleDateString(); } catch { return String(value); }
}

function fmtDateTime(value) {
  if (!value) return "—";
  try { return new Date(value).toLocaleString(); } catch { return String(value); }
}

export default function HistoryDrawer({ connectedAccount, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const id = connectedAccount?.id;
  const isGA4 = connectedAccount?.providerAccountKind === "ga4_property";

  useEffect(() => {
    if (!open || !id) return;
    setLoading(true);
    setError(null);
    setData(null);
    InsightsService.getHistory(id)
      .then((res) => setData(res))
      .catch((e) => setError(e?.response?.data?.message || e.message || "Failed to load history."))
      .finally(() => setLoading(false));
  }, [open, id]);

  const history = data?.history || [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: "100vw", sm: 560 } } }}>
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h3" noWrap>{connectedAccount?.displayName}</Typography>
            <Typography variant="caption" color="text.secondary">Sync history (last 10 syncs)</Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {loading ? (
            <Stack alignItems="center" sx={{ pt: 6 }}>
              <CircularProgress size={32} />
            </Stack>
          ) : error ? (
            <Typography color="error" variant="body2">{error}</Typography>
          ) : history.length === 0 ? (
            <Box sx={{ pt: 6, textAlign: "center" }}>
              <Typography variant="subtitle1">No history yet</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Sync this account to start tracking history.
              </Typography>
            </Box>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Synced at</TableCell>
                  <TableCell>Window</TableCell>
                  <TableCell align="right">{isGA4 ? "Sessions" : "Reach"}</TableCell>
                  <TableCell align="right">{isGA4 ? "Users" : "Followers"}</TableCell>
                  <TableCell align="right">{isGA4 ? "Conversions" : "Engagements"}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.map((row) => {
                  const m = row.metrics || {};
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography variant="body2">{fmtDateTime(row.fetchedAt)}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtDate(row.windowStart)} – {fmtDate(row.windowEnd)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip size="small" label={row.window || "30d"} variant="outlined" />
                      </TableCell>
                      <TableCell align="right">{fmt(isGA4 ? m.sessions : m.reach)}</TableCell>
                      <TableCell align="right">{fmt(isGA4 ? m.users : m.followers)}</TableCell>
                      <TableCell align="right">{fmt(isGA4 ? m.conversions : m.engagements)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
